import React, { useState, useEffect, useCallback } from "react";
import AppModal from "../../AppModal";
import AppModalHeader from "../../AppModal/AppModalHeader";
import AppButton from "../../AppButton";
import AppSelect from "../../AppSelect";
import { fetcher } from "../../../services/api";
import AppToggle from "../../AppToggle";
import AppInput from "../../AppInput";
import AppModalContent from "../../AppModal/AppModalContent";
import { AppData } from "../ApiKeysTable";
import { useValidationErrors } from "../../../hooks/useValidationErrors";
import Loader from "../../Loader/Loader";
import "./AddPlatformModal.css";

interface AddPortalModalProps {
  isVisible: boolean;
  setIsModalVisible: (visible: boolean) => void;
  groupKey?: string | null;
  onPlatformCreate?: () => void;
  editData?: AppData | null;
}

const AddPortalModal: React.FC<AddPortalModalProps> = ({
  isVisible,
  setIsModalVisible,
  groupKey,
  onPlatformCreate,
  editData,
}) => {
  const [formData, setFormData] = useState<{
    display_name: string;
    ad_store_url: string;
    ads_enabled: boolean;
    ad_bundle: string;
    advertisement_position: number | null;
    ad_app_name: string;
  }>({
    display_name: "",
    ad_store_url: "",
    ads_enabled: false,
    ad_bundle: "",
    advertisement_position: null,
    ad_app_name: "",
  });
  const { errors, setApiErrors, clearError } =
    useValidationErrors<typeof formData>();
  const [loading, setLoading] = useState(false);
  const [invalidAdUrl, setInvalidAdUrl] = useState(true);

  const updateFormData = useCallback(
    (key: keyof typeof formData, value: any) => {
      setFormData((prev) => ({ ...prev, [key]: value }));
      clearError(key);
    },
    [clearError]
  );

  useEffect(() => {
    if (!isVisible || !formData.ads_enabled || !formData.ad_store_url) return;

    const fetchAdInfo = async () => {
      try {
        const encodedURL = encodeURIComponent(formData.ad_store_url);

        const response = await fetcher<any>(
          `/publisher/client/get-advertisement-data?endpoint=${encodedURL}`,
          { method: "GET" }
        );

        const bundleId = response?.data?.data?.id;
        const title = response?.data?.data?.title;

        if (bundleId && bundleId !== formData.ad_bundle) {
          updateFormData("ad_bundle", bundleId);
        }

        if (title && title !== formData.ad_app_name) {
          updateFormData("ad_app_name", title);
        }
      } catch (error) {
        console.error("Error fetching advertisement data:", error);
      }
    };

    fetchAdInfo();
  }, [
    isVisible,
    formData.ads_enabled,
    formData.ad_store_url,
    formData.ad_bundle,
    formData.ad_app_name,
    updateFormData,
  ]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      group_key: groupKey,
      display_name: formData.display_name,
      ads_enabled: formData.ads_enabled,
      ad_store_url: formData.ad_store_url,
      ad_bundle: formData.ad_bundle,
      ad_app_name: formData.ad_app_name,
      advertisement_position: Number(formData.advertisement_position ?? 3),
    };

    if (editData) {
      const { data, error } = await fetcher<any>(
        `/publisher/client/platform/update/${editData.key}`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      if (data) {
        onPlatformCreate?.();
        resetForm();
        setIsModalVisible(false);
        setLoading(false);
      } else {
        setApiErrors(error);
        setLoading(false);
      }
    } else {
      const { data, error } = await fetcher<any>(
        "/publisher/client/platform/create",
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      if (data) {
        onPlatformCreate?.();
        resetForm();
        setIsModalVisible(false);
        setLoading(false);
      } else {
        setApiErrors(error);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (isVisible && editData) {
      setFormData({
        display_name: editData.display_name || "",
        ad_store_url: editData.ad_store_url || "",
        ads_enabled: editData.ads_enabled,
        ad_bundle: editData.ad_bundle || "",
        advertisement_position: editData.advertisement_position || 1,
        ad_app_name: editData.ad_app_name || "",
      });
    } else if (isVisible && !editData) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editData, isVisible]);

  const resetForm = () => {
    setFormData({
      display_name: "",
      ad_store_url: "",
      ads_enabled: false,
      ad_bundle: "",
      advertisement_position: null,
      ad_app_name: "",
    });
    setApiErrors({});
    setInvalidAdUrl(false);
  };

  const isValidStoreUrl = (url: string) => {
    if (!url.trim()) return true;
    return (
      url.includes("play.google.com/store/apps/details") ||
      url.includes("apps.apple.com")
    );
  };

  return (
    <AppModal
      isVisible={isVisible}
      onChange={() => {
        setIsModalVisible(!isVisible);
        resetForm();
      }}
      className="add-filter-modal"
    >
      <AppModalHeader
        onClose={() => {
          setIsModalVisible(!isVisible);
          resetForm();
        }}
      >
        {editData ? "Update API Key" : "Create API Key"}
      </AppModalHeader>
      <AppModalContent>
        <div className="modal-description">
          Provide some basic information to set up your app for integration.
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <AppInput
            id="display_name"
            label="Key Name (e.g. KLIPY-iOS, KLIPY-WEB etc.)"
            value={formData.display_name}
            onChange={(e) => updateFormData("display_name", e.target.value)}
            error={errors.display_name}
          />
          <AppInput
            id="ad_store_url"
            label="URL (Website, Playstore, Appstore etc.)"
            value={formData.ad_store_url}
            onChange={(e) => {
              const value = e.target.value;
              updateFormData("ad_store_url", value);
              setInvalidAdUrl(isValidStoreUrl(value));
              setFormData((prev) => ({
                ...prev,
                ad_bundle: "",
                ad_app_name: "",
              }));
            }}
            error={errors.ad_store_url}
          />
          <div className="add-platform-revenueapi">
            <span>Want to start earning? Enable the Ads API</span>
            <AppToggle
              checked={formData.ads_enabled}
              onChange={(checked: boolean) => {
                updateFormData("ads_enabled", checked);
                if (formData.ad_store_url === "") {
                  setInvalidAdUrl(true);
                }
              }}
              text={formData.ads_enabled ? "ON" : "OFF"}
            />
          </div>

          {formData.ads_enabled && (
            <>
              {invalidAdUrl && (
                <>
                  <AppInput
                    id="ad_bundle"
                    label="Application Bundle"
                    value={formData.ad_bundle}
                    onChange={(e) =>
                      updateFormData("ad_bundle", e.target.value)
                    }
                    error={errors.ad_bundle}
                  />

                  <AppInput
                    id="ad_app_name"
                    label="Application Name"
                    value={formData.ad_app_name}
                    onChange={(e) =>
                      updateFormData("ad_app_name", e.target.value)
                    }
                    error={errors.ad_app_name}
                  />
                </>
              )}

              <div className="field-wrapper">
                <div className="ad-position-description-text">
                  Ad position defines the ad's display location in the content
                  grid.&nbsp;
                  <a
                    href="https://docs.klipy.com/advertisements/receiving-an-ad"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </a>
                </div>
                <AppSelect
                  options={[
                    { value: 4, name: "4th" },
                    { value: 3, name: "3rd" },
                    { value: 2, name: "2nd" },
                    { value: 1, name: "1st" },
                  ]}
                  placeholder="Advertisement Position"
                  single
                  noFlag
                  value={[formData.advertisement_position ?? ""]}
                  isDark
                  onSelect={(valueArray) =>
                    updateFormData(
                      "advertisement_position",
                      Number(valueArray[0])
                    )
                  }
                />
              </div>
            </>
          )}
          <AppButton
            variant="secondary"
            type="submit"
            fullWidth
            disabled={loading}
          >
            {loading ? <Loader /> : editData ? "Update" : "Create"}
          </AppButton>
        </form>
      </AppModalContent>
    </AppModal>
  );
};

export default AddPortalModal;
